import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from "recharts";
import { Payload } from "recharts/types/component/DefaultTooltipContent";
import { Typography } from "@mui/material";

const customStyles = {
  tooltipContainer: {
    backgroundColor: "rgba(255, 255, 255, 1)",
    border: "1px solid rgba(218, 218, 218, 1)",
    borderRadius: "4px",
    boxShadow: "0 5px 10px 0 rgba(0, 0, 0, 0.06)",
    padding: "10px",
    "& p": {
      margin: 0,
    },
  },
  legendContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "10px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    margin: "0 10px",
    cursor: "pointer",
  },
  legendItemTitle: {
    color: "rgba(81, 81, 81, 1)",
    padding: "0 10px",
  },
};

interface ILegend {
  color?: string;
  dataKey?: string | number;
  inactive?: boolean;
  payload?: {
    dataKey: string;
    fill: string;
    legendColor: string;
  };
  type?: string;
  value?: string;
}
type brushDimension = {
  x?: number;
  width?: number;
};

type dataKey = { color: string; label: string; legendColor?: string };
export type dataValue = {
  [key: string]: number | string;
} & { xAxisLabel: string; title: string; selectedItem: string };

const size = {
  large: {
    width: "37",
    height: "22",
  },
  small: {
    width: "15",
    height: "10",
  },
};

interface IBarsGroupChartProps {
  dataKeys: dataKey[];
  data: dataValue[];
  xAxisLabel: string;
  barCategoryGap: string;
  clickHandler?(item: any): void;
  cursorValue: string;
  isPrinting?: boolean;
  label?: "top";
  radius?: number | [number, number, number, number];
  legendSize?: "large" | "small";
}
export default function BarsGroupChart({
  dataKeys,
  data,
  xAxisLabel,
  barCategoryGap = "10%",
  clickHandler,
  cursorValue,
  isPrinting = false,
  label,
  radius,
  legendSize = "large",
}: Readonly<IBarsGroupChartProps>) {
  const { i18n, t } = useTranslation();

  const [updatedDataKeys, setUpdatedDataKeys] = useState<dataKey[]>([]);
  const [brushDimension, setBrushDimensions] = useState<brushDimension>({});

  useEffect(() => {
    let newDataKeys = [...dataKeys];
    newDataKeys = newDataKeys.map((item) => ({
      ...item,
      legendColor: item.color,
    }));
    setUpdatedDataKeys(newDataKeys);
  }, [dataKeys]);

  useEffect(() => {
    handleOrientation();
    window.addEventListener("orientationchange", handleOrientation);
    return () => {
      window.removeEventListener("orientationchange", handleOrientation);
    };
  }, []);

  const handleOrientation = () => {
    setBrushDimensions({
      width: (window.screen.width * 55) / 100,
      x:
        window.screen.width >= 1400
          ? 290
          : window.screen.width >= 1024
          ? 220
          : 140,
    });
  };

  const renderLegend = ({ payload }: { payload: ILegend[] | undefined }) => {
    return (
      <div style={customStyles.legendContainer}>
        {payload?.map((item, index) => (
          <div
            style={customStyles.legendItem}
            key={"item" + index}
            onClick={() => legendClickHandler(item)}
          >
            <svg
              width={size[legendSize].width}
              height={size[legendSize].height}
            >
              <rect
                width={size[legendSize].width}
                height={size[legendSize].height}
                rx="3"
                style={{
                  fill: updatedDataKeys.find(
                    (dataK) => dataK.label === item.dataKey
                  )?.legendColor,
                }}
              />
            </svg>
            <span style={customStyles.legendItemTitle}>{item.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const legendClickHandler = (legend: ILegend) => {
    let dataKeys = updatedDataKeys.map((item) => ({
      ...item,
      color:
        legend.dataKey === item.label
          ? item.color === "rgba(255 255 255/0%)"
            ? item.legendColor!
            : "rgba(255 255 255/0%)"
          : item.color,
      legendColor: item.legendColor,
    }));
    setUpdatedDataKeys(dataKeys);
  };

  const barData = updatedDataKeys.map((item, index) => (
    <Bar
      key={"group" + index}
      dataKey={item.label}
      fill={item.color}
      cursor={cursorValue}
      radius={radius}
      isAnimationActive={!isPrinting}
      label={
        label
          ? {
              position: label,
              fontSize: legendSize === "large" ? "16px" : "12px",
              fill: "rgb(111, 111, 111)",
              fontWeight: "bold",
              offset: 12,
            }
          : undefined
      }
    />
  ));

  const renderTooltip = ({
    active,
    payload,
  }: {
    active?: boolean;
    payload?: any;
  }) => {
    if (active && payload?.length) {
      return (
        <Typography
          component="div"
          sx={{ ...customStyles.tooltipContainer, direction: i18n.dir() }}
        >
          <p style={{ fontSize: "14px" }}>{`${payload[0].payload.title}`}</p>
          {payload.map((item: any, index: number) => (
            <p
              key={"tooltip" + index}
              style={{
                fontSize: "11px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <span style={{ flexGrow: 1 }}>{`${item.dataKey}:`}</span>
              <span>&nbsp; &nbsp;{payload[0].payload[item.dataKey]}</span>
            </p>
          ))}
          <p
            style={{ fontSize: "11px", display: "flex", alignItems: "center" }}
          >
            <span style={{ flexGrow: 1 }}>{t("Pacing Ratio")}</span>
            <span>{calculateRatio(payload[0].value, payload[1].value)}</span>
          </p>
        </Typography>
      );
    }
    return undefined;
  };
  const sortedData = [...data].sort((a, b) => {
    return 1;
  });

  return (
    <ResponsiveContainer>
      <BarChart
        data={sortedData}
        maxBarSize={50}
        barCategoryGap={barCategoryGap}
        onClick={clickHandler}
        style={{ fontSize: legendSize === "large" ? "16px" : "12px" }}
        margin={{
          top: 40,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxisLabel} />
        <YAxis
          orientation={i18n.dir() === "rtl" ? "right" : "left"}
          domain={[0, 100]}
        />
        <Tooltip content={renderTooltip} />
        <Legend
          verticalAlign="top"
          wrapperStyle={{ top: -20 }}
          content={({ payload }) =>
            renderLegend({ payload } as { payload: Payload<string, string>[] })
          }
        />
        <ReferenceLine y={0} stroke="#000" />
        {isPrinting ||
        (data.length <= 6 && window.screen.width > 1024) ? null : (
          <Brush
            dataKey={xAxisLabel}
            height={30}
            stroke="#8884d8"
            x={brushDimension.x}
            width={brushDimension.width}
          />
        )}
        {barData}
      </BarChart>
    </ResponsiveContainer>
  );
}

const calculateRatio = (a: number, b: number): string => {
  if (a === 0 || b === 0) return "0%";
  const ratio = a < b ? a / b : b / a;
  return `${Math.round(ratio * 100)}%`;
};
